/**
 * AIOE (D401, 0.90.0): the organisation's half of workflows, beside the workbench-owned contract (./workflows.ts, mirrored and
 * never edited here). This file is the platform's and the workbench never mirrors it: the `workflows` policy clause as the
 * catalogue reads it, the shape of a run report and its answer, and what the console reads.
 *
 * Routes (docs/workbench-integration.md, section "Workflows (D401)"). The contract's, at a project's home:
 *
 *   GET    /workflows/v1/:projectId?q&status                           {@link WorkflowCatalogue} (the contract's)
 *   POST   /workflows/v1/:projectId                                    { manifest } → 201 the listing; 409 `version_exists`, `taken`;
 *                                                                      403 `publishing_off`, `scripts_not_allowed`; 400 `invalid`
 *   GET    /workflows/v1/:projectId/:workflowId                        { versions }, newest first
 *   GET    /workflows/v1/:projectId/:workflowId/versions/:version      the listing, with the definition to add
 *   POST   /workflows/v1/:projectId/:workflowId/versions/:version/review  { decision, reason? } → the listing
 *   POST   /workflows/v1/:projectId/:workflowId/withdraw               { withdrawnAt }
 *   POST   /workflows/v1/:projectId/:workflowId/versions/:version/install  { hash? } → { installs }; 409 `changed`
 *   POST   /workflows/v1/:projectId/runs                               { runs[], steps[] } (at most 500 records) → {@link WorkflowRunsReportAnswer}
 *   GET    /workflows/v1/:projectId/runs?from=&to=&workflowId=         {@link WorkflowRunsPage}
 *
 * The platform's own, for the console and the phone:
 *
 *   GET    /workflows/v1/:projectId/runs/:runId                        {@link WorkflowRunDetail}
 *   POST   /workflows/v1/:projectId/runs/:runId/decide                 {@link WorkflowRunDecision} → { ok, message }: relayed to the machine
 *   GET    /workflows/v1/:projectId/:workflowId/machines               { machines: {@link WorkflowMachine}[] }: where Run now may start it
 *   POST   /workflows/v1/:projectId/:workflowId/start                  {@link WorkflowStartRequest} → 201 {@link WorkflowStartAnswer}
 *   GET    /admin/v1/workflows                                         every version in the organisation ({@link OrganisationWorkflows})
 *   GET    /admin/v1/workflow-runs?from=&to=&projectId=&workflowId=    {@link OrganisationWorkflowRuns}
 *
 * A workflow id `runs` is refused at publish: `/workflows/v1/:projectId/runs` is the run records' address.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
/** How long run and step records are kept after their last report (the design's "Kept 90 days, like runs"). */
export const WORKFLOW_RUN_KEEP_DAYS = 90;
/** Records in one report: runs and steps together. */
export const WORKFLOW_REPORT_MAX = 500;
/** Runs one read answers at most; the newest are kept. */
export const WORKFLOW_RUNS_READ_MAX = 500;
/** A Run now order nobody answered is dropped after this long (a desktop asleep overnight still starts it when it wakes). */
export const WORKFLOW_START_ORDER_TTL_MS = 12 * 3600 * 1000;
/** Ids a workflow may not take: they are addresses under `/workflows/v1/:projectId`. */
export const WORKFLOW_RESERVED_IDS = ["runs"];
// The `workflows` policy clause (workflowsPolicySchema, effectiveWorkflowsPolicy, workflowPolicyRefusal) is the
// workbench's, in ./workflows.ts: the machines enforce the same reading.
// ── Run reports ────────────────────────────────────────────────────────
/** `POST /workflows/v1/:projectId/runs`: each record is read by itself, so one that does not parse never stops the rest. */
export const workflowRunsReportSchema = z
    .object({
    runs: z.array(z.unknown()).max(WORKFLOW_REPORT_MAX).default([]),
    steps: z.array(z.unknown()).max(WORKFLOW_REPORT_MAX).default([]),
})
    .strict()
    .refine((r) => r.runs.length + r.steps.length <= WORKFLOW_REPORT_MAX, `a report carries at most ${WORKFLOW_REPORT_MAX} records, runs and steps together`);
export const workflowRunDecisionSchema = z
    .object({
    stepId: z.string().regex(/^[a-z][a-z0-9-]{0,40}$/),
    attempt: z.number().int().min(1),
    decision: z.enum(["approved", "rejected", "held"]),
    reason: z.string().max(1000).optional(),
})
    .strict();
export const workflowStartRequestSchema = z
    .object({
    deviceId: z.string().min(1).max(100),
    /** A listed version; absent, the newest listed. */
    version: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/).optional(),
    inputs: z.record(z.string(), z.unknown()).default({}),
    rootId: z.string().max(200).optional(),
})
    .strict();
/** The inputs a run is started with, checked against what the workflow asks: the first problem, in words, or undefined. */
export function workflowInputsProblem(wf, inputs) {
    const asked = new Map(wf.inputs.map((i) => [i.name, i]));
    for (const name of Object.keys(inputs))
        if (!asked.has(name))
            return `It does not ask for ${name}.`;
    for (const i of wf.inputs) {
        const v = inputs[i.name];
        if (v === undefined || v === null || v === "") {
            if (i.required)
                return `Say ${i.label.replace(/[?.]$/, "").toLowerCase()}.`;
            continue;
        }
        if (i.type === "text" && typeof v !== "string")
            return `${i.label} is text.`;
        if (i.type === "number" && (typeof v !== "number" || !Number.isFinite(v)))
            return `${i.label} is a number.`;
        if (i.type === "boolean" && typeof v !== "boolean")
            return `${i.label} is yes or no.`;
    }
    return undefined;
}
/** The risk the Inbox shows for an approval of this class (workflows design: read 2, change 3, high impact 4). */
export const APPROVAL_RISK = { read: 2, change: 3, "high-impact": 4 };
//# sourceMappingURL=workflowsPlatform.js.map